<template>
  <div
    class="block-card"
    :class="{ selected, 'in-canvas': isInCanvas }"
    @click="$emit('select', bloc)"
    @dblclick="$emit('edit', bloc)"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <div class="block-thumbnail">
      <img
        v-if="displayUrl"
        :src="displayUrl"
        :alt="bloc.object_name"
        @error="handleImageError"
      />
      <div v-if="isInCanvas" class="in-canvas-badge">
        <span class="material-symbols-outlined">check_circle</span>
      </div>
      <div v-if="blocKind === 'avatar'" class="avatar-badge" :title="avatarBadgeTitle">
        <span class="material-symbols-outlined">palette</span>
      </div>
    </div>
    <div class="block-info">
      <div class="block-name">
        <span v-html="highlightText(bloc.object_name, searchHighlight)"></span>
      </div>
      <div v-if="bloc.inventory_number" class="block-inventory">
        [<span v-html="highlightText(bloc.inventory_number, searchHighlight)"></span>]
      </div>
      <div v-if="bloc.fragment_identification" class="block-ident" :title="bloc.fragment_identification">
        <span v-html="highlightText(truncate(bloc.fragment_identification, 72), searchHighlight)"></span>
      </div>
      <div v-if="bloc.poids_estime_kg != null" class="block-weight">
        ~{{ formatPoids(bloc.poids_estime_kg) }} kg
      </div>
      <div class="block-actions">
        <button
          v-if="blocKind === 'bloc'"
          type="button"
          class="block-action-btn"
          title="Créer un avatar"
          @click.stop="handleCreateAvatar"
        >
          <span class="material-symbols-outlined">palette</span>
          Avatar
        </button>
        <button
          type="button"
          class="block-action-btn"
          :disabled="!hasComposite"
          :title="hasComposite ? 'Voir la photo composite' : 'Aucune photo composite'"
          @click.stop="openComposite"
        >
          <span class="material-symbols-outlined">photo_library</span>
          Composite
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getApiBase } from '@api/client';
import type { Bloc } from '@api/blocs.api';
import { getCachedBlockImage } from '../composables/useBlockImageCache';

const props = defineProps<{
  bloc: Bloc;
  selected: boolean;
  isInCanvas?: boolean;
  searchHighlight?: string;
}>();

const apiUrl = getApiBase();
const router = useRouter();
const emit = defineEmits<{
  select: [bloc: Bloc];
  edit: [bloc: Bloc];
  createAvatar: [bloc: Bloc];
}>();

const thumbPath = computed(() => {
  const p = props.bloc.image_path;
  const filename = p.includes('/') ? p.slice(p.lastIndexOf('/') + 1) : p;
  return `/images/blocs/thumb/${filename}`;
});
const fullImageUrl = computed(() => `${apiUrl}${props.bloc.image_path}`);
const blocKind = computed<'bloc' | 'avatar'>(() =>
  props.bloc.bloc_kind === 'avatar' ? 'avatar' : 'bloc'
);
const hasComposite = computed(() => Boolean(props.bloc.composite_slug));

const displayUrl = ref('');
let ownedBlobUrl: string | null = null;

async function loadFromCache(): Promise<void> {
  if (ownedBlobUrl) {
    URL.revokeObjectURL(ownedBlobUrl);
    ownedBlobUrl = null;
  }

  const version = props.bloc.updated_at ?? '';
  const blobUrl =
    (await getCachedBlockImage(thumbPath.value, version)) ??
    (await getCachedBlockImage(props.bloc.image_path, version));
  if (blobUrl) {
    ownedBlobUrl = blobUrl;
    displayUrl.value = blobUrl;
    return;
  }
  displayUrl.value = fullImageUrl.value;
}

// Charger au montage et si le bloc change
watch(() => props.bloc.id, loadFromCache, { immediate: true });

onUnmounted(() => {
  if (ownedBlobUrl) URL.revokeObjectURL(ownedBlobUrl);
});

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  if (img.src !== fullImageUrl.value) {
    img.src = fullImageUrl.value;
  } else {
    img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23333" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
  }
};


const handleDragStart = (event: DragEvent) => {
  if (event.dataTransfer) {
    event.dataTransfer.setData('bloc-id', props.bloc.id.toString());
    event.dataTransfer.effectAllowed = 'copy';
  }
  
  // Notifier le canvas qu'un drag commence
  window.dispatchEvent(new CustomEvent('bloc-drag-start'));
};

const avatarBadgeTitle = computed(() => {
  if (!props.bloc.avatar_color) return 'Avatar';
  return `Avatar ${props.bloc.avatar_color}`;
});

const handleCreateAvatar = () => {
  emit('createAvatar', props.bloc);
};

function openComposite() {
  if (!hasComposite.value) return;
  router.push({ name: 'bloc-composite', params: { id: String(props.bloc.id) } });
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function highlightText(text: string, query?: string): string {
  const safe = escapeHtml(text);
  if (!query?.trim()) return safe;
  const q = escapeHtml(query.trim());
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return safe.replace(re, '<span class="search-highlight">$1</span>');
}

function truncate(s: string, max: number): string {
  const t = s.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function formatPoids(n: number): string {
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return Number.isInteger(x) ? String(x) : x.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

const handleDragEnd = () => {
  // Notifier le canvas que le drag est terminé
  window.dispatchEvent(new CustomEvent('bloc-drag-end'));
};
</script>

<style scoped>
.block-card {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.block-card:hover {
  background-color: #3a3a3a;
  border-color: #555;
}

.block-card.selected {
  background-color: #2a4a6a;
  border-color: #4a9eff;
}

.block-thumbnail {
  width: 100%;
  height: 120px;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  background-color: #1a1a1a;
  position: relative;
  cursor: pointer;
}

.block-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.in-canvas-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(40, 167, 69, 0.9);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.avatar-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background-color: rgba(0, 0, 0, 0.65);
  border: 1px solid #6a6a6a;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.avatar-badge .material-symbols-outlined {
  font-size: 18px;
  color: #ffd166;
}

.in-canvas-badge .material-symbols-outlined {
  font-size: 20px;
  color: white;
}

.block-info {
  font-size: 0.85rem;
}

.block-name {
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 0.25rem;
}

.block-inventory {
  color: #999;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.block-ident {
  color: #aaa;
  font-size: 0.72rem;
  line-height: 1.25;
  margin-bottom: 0.2rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.block-weight {
  color: #7eb8ff;
  font-size: 0.75rem;
  font-weight: 500;
}

.block-actions {
  margin-top: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.block-action-btn {
  border: 1px solid #4f4f4f;
  background-color: #272727;
  color: #d8d8d8;
  border-radius: 4px;
  padding: 0.2rem 0.45rem;
  font-size: 0.72rem;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  cursor: pointer;
}

.block-action-btn:hover:not(:disabled) {
  border-color: #6a6a6a;
  background-color: #313131;
}

.block-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.block-action-btn .material-symbols-outlined {
  font-size: 15px;
}

.search-highlight {
  background-color: rgba(74, 158, 255, 0.4);
  border-radius: 2px;
  padding: 0 1px;
}
</style>
